/**
 * PastMeetings — Table of completed meetings with server-side filters and pagination
 */

import { useState, useMemo, useEffect } from 'react';
import { ChevronDown, ChevronUp, Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { MeetingsTable, type ColumnDef } from './MeetingsTable';
import { CrmCompanyChip } from './CrmCompanyChip';
import { useMeetings } from '../hooks/useMeetings';
import { useStaffUserMap } from '../hooks/useStaffUsers';
import { useDebounce } from '@/hooks/useDebounce';
import type { Meeting, MatchStatus } from '../types';

// =============================================================================
// Helpers
// =============================================================================

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-CA', {
    timeZone: 'America/Montreal',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

function formatDuration(startedAt: string | null, endedAt: string | null): string {
  if (!startedAt || !endedAt) return '—';
  const ms = new Date(endedAt).getTime() - new Date(startedAt).getTime();
  const minutes = Math.round(ms / 60000);
  if (minutes < 1) return '< 1 min';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainingMin = minutes % 60;
  return remainingMin > 0 ? `${hours} hr ${remainingMin} min` : `${hours} hr`;
}

function SourceBadge({ source }: { source: string | null }) {
  if (!source) return null;
  const label = source === 'google_meet' ? 'Google Meet' : source;
  return (
    <span className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground">
      {label}
    </span>
  );
}

function RecordingBadge({ hasRecording }: { hasRecording: boolean }) {
  if (!hasRecording) return <span className="text-muted-foreground/40">—</span>;
  return (
    <span className="inline-block h-2 w-2 rounded-full bg-green-500" aria-label="Recording available" title="Recording available" />
  );
}

// =============================================================================
// PastMeetings
// =============================================================================

interface ColumnFilters {
  date: string;
  duration: string;
  owner: string;
}

interface PastMeetingsProps {
  teamId?: string;
  onSelectMeeting?: (id: string) => void;
}

const PAGE_SIZE = 15;

export function PastMeetings({ teamId, onSelectMeeting }: PastMeetingsProps) {
  const [page, setPage] = useState(1);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Server-side filters
  const [searchInput, setSearchInput] = useState('');
  const [matchStatusFilter, setMatchStatusFilter] = useState<MatchStatus | ''>('');
  const [scopeFilter, setScopeFilter] = useState<'customer' | 'internal' | ''>('');

  // Column-level client-side filters (narrow within the current page).
  // Note: title search is intentionally omitted — use the server-side `q` box
  // above the table which searches across all pages.
  const [filters, setFilters] = useState<ColumnFilters>({
    date: '', duration: '', owner: '',
  });

  const debouncedSearch = useDebounce(searchInput, 300);

  // Reset to page 1 when server-side filters change
  useEffect(() => { setPage(1); }, [debouncedSearch, matchStatusFilter, scopeFilter, teamId]);

  const { data, isLoading } = useMeetings({
    status: 'completed',
    team_id: teamId,
    pageNumber: page - 1,
    pageLength: PAGE_SIZE,
    sortKey: 'scheduled_at',
    sortDirection,
    ...(debouncedSearch ? { q: debouncedSearch } : {}),
    ...(matchStatusFilter ? { matchStatus: matchStatusFilter } : {}),
    ...(scopeFilter ? { scope: scopeFilter } : {}),
  });

  const userMap = useStaffUserMap();

  const meetings = data?.meetings ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function resolveOwner(ownerId: string | null): string {
    if (!ownerId) return '—';
    const u = userMap.get(ownerId);
    if (!u) return '—';
    return [u.firstname, u.lastname].filter(Boolean).join(' ') || u.email;
  }

  function setFilter(col: keyof ColumnFilters, value: string) {
    setFilters((prev) => ({ ...prev, [col]: value }));
  }

  function clearFilters() {
    setFilters({ date: '', duration: '', owner: '' });
    setSearchInput('');
    setMatchStatusFilter('');
    setScopeFilter('');
  }

  function toggleSort() {
    setSortDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'));
    setPage(1);
  }

  // Client-side column filtering on the current page (date/duration/owner only).
  // Title search is server-side via the `q` box above the table.
  const filtered = useMemo(() => {
    const f = filters;
    if (!f.date && !f.duration && !f.owner) return meetings;
    return meetings.filter((m) => {
      if (f.date && !formatDate(m.scheduled_at).includes(f.date)) return false;
      if (f.duration) {
        const dur = formatDuration(m.started_at, m.ended_at).toLowerCase();
        if (!dur.includes(f.duration.toLowerCase())) return false;
      }
      if (f.owner && !resolveOwner(m.owner_id).toLowerCase().includes(f.owner.toLowerCase())) return false;
      return true;
    });
  }, [meetings, filters, userMap]);

  const hasFilters =
    Object.values(filters).some(Boolean) ||
    !!searchInput ||
    !!matchStatusFilter ||
    !!scopeFilter;

  const columns: ColumnDef<Meeting>[] = [
    {
      id: 'date',
      header: (
        <button
          className="flex items-center gap-1 hover:text-foreground transition-colors"
          onClick={toggleSort}
        >
          Date
          {sortDirection === 'desc' ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronUp className="h-3.5 w-3.5" />
          )}
        </button>
      ),
      filterPlaceholder: 'yyyy-mm-dd',
      filterValue: filters.date,
      onFilterChange: (v) => setFilter('date', v),
      cell: (m) => formatDate(m.scheduled_at),
      cellClassName: 'font-medium',
    },
    {
      id: 'meeting',
      header: 'Meeting',
      cell: (m) => (
        <span className="font-medium">
          {m.source && <SourceBadge source={m.source} />}
          {m.source ? ' ' : ''}
          {m.title}
        </span>
      ),
      cellClassName: 'font-medium',
    },
    {
      id: 'rec',
      header: 'Rec',
      cell: (m) => <RecordingBadge hasRecording={!!m.recording_url} />,
      cellClassName: 'text-center',
    },
    {
      id: 'company',
      header: 'Company',
      cell: (m) => (
        m.match_status ? (
          <CrmCompanyChip
            matchStatus={m.match_status}
            matchedCompany={m.matched_company}
          />
        ) : null
      ),
    },
    {
      id: 'duration',
      header: 'Duration',
      filterPlaceholder: 'Search…',
      filterValue: filters.duration,
      onFilterChange: (v) => setFilter('duration', v),
      cell: (m) => formatDuration(m.started_at, m.ended_at),
      cellClassName: 'text-muted-foreground',
    },
    {
      id: 'owner',
      header: 'Owner',
      filterPlaceholder: 'Search…',
      filterValue: filters.owner,
      onFilterChange: (v) => setFilter('owner', v),
      cell: (m) => resolveOwner(m.owner_id),
      cellClassName: 'text-muted-foreground text-sm',
    },
  ];

  return (
    <div className="space-y-3">
      {/* Server-side filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Global search — server-side, crosses all pages */}
        <div className="relative flex-1 min-w-48 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            className="pl-8 h-8 text-sm"
            placeholder="Search meetings, attendees, company…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search meetings"
          />
          {searchInput && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setSearchInput('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Scope toggle */}
        <div
          role="group"
          aria-label="Scope filter"
          className="flex items-center rounded-md border border-border text-xs overflow-hidden"
        >
          {(['', 'customer', 'internal'] as const).map((v) => (
            <button
              key={v}
              type="button"
              aria-pressed={scopeFilter === v}
              onClick={() => setScopeFilter(v)}
              className={`px-3 py-1.5 transition-colors ${scopeFilter === v ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'}`}
            >
              {v === '' ? 'All' : v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>

        {/* Match status filter */}
        <div
          role="group"
          aria-label="CRM match status filter"
          className="flex items-center rounded-md border border-border text-xs overflow-hidden"
        >
          {([
            { value: '' as const, label: 'All' },
            { value: 'matched' as MatchStatus, label: 'Matched' },
            { value: 'ambiguous' as MatchStatus, label: 'Unsure' },
            { value: 'unmatched' as MatchStatus, label: 'Unmatched' },
          ]).map(({ value, label }) => (
            <button
              key={value}
              type="button"
              aria-pressed={matchStatusFilter === value}
              onClick={() => setMatchStatusFilter(value)}
              className={`px-3 py-1.5 transition-colors ${matchStatusFilter === value ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'}`}
            >
              {label}
            </button>
          ))}
        </div>

        {hasFilters && (
          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={clearFilters}>
            Clear all
          </Button>
        )}
      </div>

      <MeetingsTable
        columns={columns}
        rows={filtered}
        rowKey={(m) => m.id}
        onRowClick={(m) => onSelectMeeting?.(m.id)}
        isLoading={isLoading}
        total={total}
        hasFilters={hasFilters}
        onClearFilters={clearFilters}
        emptyState={
          <div className="flex flex-col items-center justify-center py-12 text-center border border-dashed border-border rounded-lg">
            <p className="text-sm text-muted-foreground">No past meetings found.</p>
          </div>
        }
        pagination={{ page, totalPages, onPageChange: setPage }}
      />
    </div>
  );
}
